import Axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import TabButton from '@/components/admin/PrintTextComparison/TabButton';
import { toneText } from '@/components/admin/PrintTextComparison/tone';
import { AnalyzedSection } from '@/components/admin/PrintTextComparison/types';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import Froala from '@/components/shared/Froala';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';

const PANE = cn(
  'tw-min-w-0 tw-rounded-lg tw-border tw-border-solid tw-border-spa-border-secondary',
  'tw-bg-spa-bg-secondary tw-p-3',
);
const PANE_TITLE = 'tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading';

// AIDEV-NOTE: Mirror of SectionEditor.tsx's Froala customConfig so the Pour Simulator's editors
// match the section-edit page's toolbar + behavior. Keep in sync — `pluginsEnabled` drives which
// native buttons render. In split layout fitEditors() grows each editor to fill the viewport; in
// tabs layout the `height` here is the fixed editor height (the panel is embedded, not full-page).
const FROALA_CONFIG = {
  height: 600,
  pastePlain: true,
  pluginsEnabled: [
    'align', 'colors', 'charCounter', 'codeBeautifier', 'codeView', 'emoticons',
    'imageManager', 'fontFamily', 'fontSize', 'inlineStyle', 'link',
    'image', 'video', 'table', 'paragraphStyle', 'paragraphFormat', 'quote',
    'lists',
  ],
  tabSpaces: 6,
  width: '100%',
};

// The student-text typography lives in student_text_combined_style_sheet.css scoped under BOTH
// `.new-section-text` and the section's theme class (e.g. "lower-el-ss"); the section edit page
// nests its editor in that stack, so we match it here for the draft to look the same.
interface FrameProps {
  children: React.ReactNode;
  // Marks which editor this is so the change list's locate can search the right pane.
  pane: 'current' | 'draft';
  themeCssName: string | null;
}

const EditorFrame = ({ children, pane, themeCssName }: FrameProps) => (
  <div className="new-section-text" data-pour-pane={pane}>
    <div className={themeCssName || undefined}>{children}</div>
  </div>
);

type Layout = 'split' | 'tabs';
type Tab = 'draft' | 'current';

interface Props {
  layout?: Layout;
  onSectionSaved: (sectionId: number, html: string) => void;
  section: AnalyzedSection;
}

const PourWorkspace = ({ layout = 'split', onSectionSaved, section }: Props) => {
  const [draft, setDraft] = useState(section.generatedHtml || '');
  const [current, setCurrent] = useState(section.currentHtml);
  const [activeTab, setActiveTab] = useState<Tab>('draft');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);

  // Split layout only: grow each editor's writing area from its top to the bottom of the viewport.
  const fitEditors = useCallback(() => {
    workspaceRef.current?.querySelectorAll<HTMLElement>('.fr-element').forEach((el) => {
      const height = Math.max(400, Math.round(window.innerHeight - el.getBoundingClientRect().top - 24));
      el.style.setProperty('min-height', `${height}px`);
    });
  }, []);

  useEffect(() => {
    if (layout !== 'split') return undefined;

    window.addEventListener('resize', fitEditors);
    return () => window.removeEventListener('resize', fitEditors);
  }, [fitEditors, layout]);

  const copyDraft = () => navigator.clipboard?.writeText(draft);

  const changeCurrent = (html: string) => {
    setCurrent(html);
    setSaved(false);
  };

  const saveCurrent = async () => {
    if (section.sectionId === null) return;

    setSaving(true);
    setSaveError(null);
    try {
      await Axios.patch(Routes.update_section_admin_print_tools_path(), {
        body_html: current,
        section_id: section.sectionId,
      });
      setSaved(true);
      onSectionSaved(section.sectionId, current);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Something went wrong updating the section.');
    } finally {
      setSaving(false);
    }
  };

  // Both editors stay mounted (edits preserved); nudge the newly-shown one to re-measure its width.
  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  };

  const setEditor = layout === 'split' ? fitEditors : undefined;

  // Saves edits made in the current-online editor, sparing the trip to the section edit page.
  // The synthetic not-online-yet row (null id) has no section to write to.
  const currentActions = section.sectionId !== null && (
    <div className="tw-flex tw-items-center tw-gap-2">
      {saved && (
        <span className={cn('tw-text-sm tw-font-bold', toneText.success)}>Section updated</span>
      )}
      {saveError && <span className={cn('tw-text-sm', toneText.error)}>{saveError}</span>}
      <SPAButton disabled={saving} size="xs" type="submit" variant="primary" onClick={saveCurrent}>
        {saving ? 'Updating…' : 'Update section'}
      </SPAButton>
    </div>
  );

  const draftEditor = section.matched ? (
    <EditorFrame pane="draft" themeCssName={section.themeCssName}>
      <Froala
        activePlugins={[]}
        customConfig={FROALA_CONFIG}
        modelId={section.sectionId ?? undefined}
        modelType="Section"
        name={`poured-${section.sectionId ?? 'opener'}`}
        setEditor={setEditor}
        text={section.generatedHtml || ''}
        userType="Admin"
        onChange={setDraft}
      />
    </EditorFrame>
  ) : (
    <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
      No matching print section was found, so there is nothing to pour here.
    </p>
  );

  const currentEditor = (
    <EditorFrame pane="current" themeCssName={section.themeCssName}>
      <Froala
        activePlugins={[]}
        customConfig={FROALA_CONFIG}
        modelId={section.sectionId ?? undefined}
        modelType="Section"
        name={`current-${section.sectionId ?? 'opener'}`}
        setEditor={setEditor}
        text={section.currentHtml}
        userType="Admin"
        onChange={changeCurrent}
      />
    </EditorFrame>
  );

  if (layout === 'tabs') {
    return (
      <div ref={workspaceRef}>
        <div className="tw-mb-3 tw-flex tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
          <TabButton active={activeTab === 'draft'} onClick={() => selectTab('draft')}>
            Generated from print (draft)
          </TabButton>
          <TabButton active={activeTab === 'current'} onClick={() => selectTab('current')}>
            Current online content
          </TabButton>
          {activeTab === 'draft' && section.matched && (
            <div className="tw-ml-auto tw-pb-2">
              <SPAButton size="xs" variant="outline" onClick={copyDraft}>Copy HTML</SPAButton>
            </div>
          )}
          {activeTab === 'current' && currentActions && (
            <div className="tw-ml-auto tw-pb-2">{currentActions}</div>
          )}
        </div>
        <div className={cn(PANE, activeTab !== 'draft' && 'tw-hidden')}>{draftEditor}</div>
        <div className={cn(PANE, activeTab !== 'current' && 'tw-hidden')}>{currentEditor}</div>
      </div>
    );
  }

  return (
    <div ref={workspaceRef} className="tw-grid tw-grid-cols-2 tw-gap-4">
      <div className={PANE}>
        <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
          <h3 className={PANE_TITLE}>Generated from print (draft)</h3>
          {section.matched && <SPAButton size="xs" variant="outline" onClick={copyDraft}>Copy HTML</SPAButton>}
        </div>
        {draftEditor}
      </div>

      <div className={PANE}>
        <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
          <h3 className={PANE_TITLE}>Current online content</h3>
          {currentActions}
        </div>
        {currentEditor}
      </div>
    </div>
  );
};

export default PourWorkspace;
